import React, { useEffect, useState } from 'react'
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import './EmptyStateGlow.scss'

/** Die vier Töne der Listen-Werkzeug-Pille, in der Folge ihrer Knöpfe (Sven, 05.10.2026). */
const GLOWS = ['task', 'section', 'note', 'memo'] as const

/**
 * Weicher Schein, der vom unteren Rand heraufleuchtet — im leeren Projekt
 * (Figma 521:112816, 25.09.2026). Er wechselt reihum die Farbe durch die Töne
 * der Knöpfe darüber: Neue Aufgabe (Violett), Abschnitt (Grün), Notiz (Gelb),
 * Memo (Cyan) — immer einer, überblendet (Sven, 05.10.2026: vier zugleich
 * waren zu viel). Dafür liegen vier gleiche Formen übereinander und bewegen
 * sich gemeinsam; nur ihre Deckkraft wechselt.
 *
 * Die Form ist ein abgerundetes Quadrat, um 45° gedreht, so breit wie der
 * Rahmen; nur ihr oberes Drittel ragt über die Unterkante. Unschärfe (Figma
 * „Layer Blur 120“ = CSS `blur(60px)`), Farben und Takt stehen im SCSS.
 *
 * Der Elternteil braucht `position: relative` und einen eigenen
 * Stapelkontext (`isolation: isolate`): Der Schein liegt mit `z-index: -1`
 * hinter allem, was im Rahmen steht.
 *
 * Er blendet nur über die Deckkraft ein, wandert und atmet danach ruhig
 * (`translate`/`scale` im SCSS) und startet, wenn es ruhig ist —
 * siehe `docs/concept/fluessige-bewegung-unter-last.md`.
 *
 * @example
 * {showEmptyStateTeaser && <EmptyStateGlow />}
 */
export const EmptyStateGlow: React.FC<{ className?: string }> = ({ className }) => {
  const reduced = useReducedMotion() ?? false
  const controls = useAnimationControls()
  // Bis zum ruhigen Moment steht auch das Wandern (`--waiting`, SCSS).
  const [calm, setCalm] = useState(false)
  // Zufälliger Einstieg in beide Bewegungen, je Besuch fest (0…2 = ein ganzer
  // Hin-und-zurück-Zyklus). Als Variablen, weil der Wert aus dem Zufall kommt.
  // Alle vier Formen teilen sie — sonst spränge der Schein beim Farbwechsel.
  const [phases] = useState(() => ({
    '--glow-drift-phase': (Math.random() * 2).toFixed(3),
    '--glow-breathe-phase': (Math.random() * 2).toFixed(3),
  }) as React.CSSProperties)
  useEffect(
    () => whenCalm(() => {
      setCalm(true)
      void controls.start({ opacity: 1, transition: { duration: reduced ? 0 : 0.9, ease: 'easeOut' } })
    }),
    [controls, reduced]
  )

  return (
    <motion.div
      className={['empty-state-glow', calm ? '' : 'empty-state-glow--waiting', className].filter(Boolean).join(' ')}
      aria-hidden="true"
      style={phases}
      initial={{ opacity: 0 }}
      animate={controls}
    >
      {GLOWS.map(glow => <GlowShape key={glow} className={`empty-state-glow__shape empty-state-glow__shape--${glow}`} />)}
    </motion.div>
  )
}

/** Die gedrehte Form aus Figma (521:112816), in `currentColor`. */
const GlowShape: React.FC<{ className: string }> = ({ className }) => (
  <svg className={className} viewBox="-92.72 114.28 597.44 597.44" preserveAspectRatio="none">
    <path
      fill="currentColor"
      d="M498.696 415.437C504.715 373.462 418.83 287.578 418.83 287.578L375.125 243.873L331.42 200.168C331.42 200.168 245.536 114.283 203.561 120.302C163.28 126.091 130.569 150.178 36.8748 243.873C-56.8198 337.567 -80.9278 370.298 -86.6959 410.559C-92.7147 452.534 -6.83014 538.418 -6.83014 538.418L36.8748 582.123L80.5797 625.828C80.5797 625.828 166.464 711.713 208.439 705.694C248.72 699.905 281.431 675.818 375.125 582.123C468.82 488.429 492.907 455.718 498.696 415.437Z"
    />
  </svg>
)
